import type { BlogPost } from '@/types'
import { Link } from '@tanstack/react-router'

interface ArticleCardProps {
  post: BlogPost
}

export function ArticleCard({ post }: ArticleCardProps) {
  return (
    <Link
      to={"/blog/$slug" as any}
      params={{ slug: post.slug } as any}
      className="group block"
    >
      <article className="h-full flex flex-col">
        {/* Image */}
        {post.featured_image && (
          <div className="aspect-[16/10] overflow-hidden rounded-lg mb-4">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}

        {/* Categories */}
        {post.categories && post.categories.length > 0 && (
          <div className="flex gap-2 mb-2">
            {post.categories.map((cat) => (
              <span
                key={cat.id}
                className="text-xs text-blue-600 font-medium uppercase tracking-wide"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h2 className="font-serif text-xl font-bold mb-2 group-hover:text-blue-600 transition-colors">
          {post.title}
        </h2>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-3">
            {post.excerpt}
          </p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-sm text-gray-500 mt-auto">
          {post.author && (
            <div className="flex items-center gap-2">
              {post.author.avatar_url && (
                <img
                  src={post.author.avatar_url}
                  alt={post.author.name}
                  className="w-6 h-6 rounded-full"
                />
              )}
              <span>{post.author.name}</span>
            </div>
          )}
          {post.published_at && (
            <>
              <span className="text-gray-300">·</span>
              <time>{new Date(post.published_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}</time>
            </>
          )}
          {post.reading_time_minutes && (
            <>
              <span className="text-gray-300">·</span>
              <span>{post.reading_time_minutes} min</span>
            </>
          )}
        </div>
      </article>
    </Link>
  )
}
